/**
 * Single source of truth for the gateway's auth surface, as consumed by the
 * frontend. Every auth fetch site (effective permissions, silent refresh)
 * references these descriptors instead of inlining a path/shape, so a
 * frontend/gateway drift surfaces in one place and is covered by a contract
 * test rather than failing silently at runtime.
 *
 * The shapes here mirror, by hand, the composed auth route surface. Ownership
 * is split across two lanes today, so the contract test scans BOTH sources:
 *
 *   GET  /api/v1/auth/me           -> { userId, permissions: string[], roles: string[] }
 *       (Tyndo.Gateway — src/Tyndo.Gateway/Endpoints/AuthEndpoints.cs)
 *   POST /api/v1/auth/refresh      body { refreshToken } -> { token, refreshToken, expiresIn }
 *       (native rotating-refresh — Tyndo.Module.Admin's NativeAuthEndpoints;
 *        this is the path the SPA actually refreshes against)
 *   POST /api/v1/auth/logout       body { refreshToken } -> 200 always
 *       (native refresh-token-family revocation — same Admin lane; the
 *        SPA logout posts here BEFORE clearing local state)
 *   POST /api/v1/auth/sso/refresh  body { refreshToken } -> { token, refreshToken, expiresIn }
 *       (OIDC/Keycloak refresh_token grant proxied by Tyndo.Gateway — a distinct
 *        path so the two never collide on the same route)
 *
 * Paths are written WITHOUT the `/api/v1` prefix because the hooks layer's
 * `resolveApiBaseUrl()` already supplies it (see apiFetch.ts). The
 * `gatewayPath()` helper reconstructs the full gateway path so the contract
 * test can assert it against the gateway's published route surface.
 */

/** The `/api/v1` prefix that `resolveApiBaseUrl()` injects for relative calls. */
export const GATEWAY_API_PREFIX = '/api/v1';

/**
 * Auth endpoints the frontend depends on, expressed as base-relative paths
 * (no `/api/v1` prefix — the API base URL resolver adds it).
 */
export const AUTH_GATEWAY_ENDPOINTS = {
  /**
   * Authoritative effective permissions + roles for the current principal,
   * resolved by the gateway from the DB-backed authorization service rather
   * than decoded from the JWT. Returns `{ userId, permissions, roles }`.
   */
  me: {
    method: 'GET',
    path: '/auth/me',
  },
  /**
   * Native rotating-refresh: exchange the persisted refresh token for a fresh
   * access token + rotated refresh token. This is the path the SPA silent
   * refresh actually posts to (see tokenRefresh.ts). Anonymous-allowlisted (the
   * access token may already be expired). Body `{ refreshToken }`, returns
   * `{ token, refreshToken, expiresIn }`.
   *
   * OWNERSHIP: implemented by Tyndo.Module.Admin's NativeAuthEndpoints
   * (`MapGroup("/api/v1/auth")` + `MapPost("/refresh")`), not the Gateway lane
   * (ADM.2 relocated native identity/session ownership out of Marketplace into
   * Admin; the wire path is unchanged). The contract test asserts this route
   * against the Admin source.
   */
  refresh: {
    method: 'POST',
    path: '/auth/refresh',
  },
  /**
   * Native logout: revoke the presented refresh token's ENTIRE family
   * server-side (single-session logout). Without this POST, "logout" only
   * clears local storage and a stolen refresh token keeps working until it
   * expires or trips replay detection. Enumeration-safe: always 200, whether
   * or not the token was valid. Body `{ refreshToken }`.
   *
   * OWNERSHIP: implemented by Tyndo.Module.Admin's NativeAuthEndpoints
   * (`MapGroup("/api/v1/auth")` + `MapPost("/logout")`), same lane as the
   * native refresh above. Driven by the AuthProvider logout via
   * `revokeSessionOnServer` (logoutRevocation.ts).
   */
  logout: {
    method: 'POST',
    path: '/auth/logout',
  },
  /**
   * SSO refresh: exchange an OIDC/Keycloak refresh token for a freshly minted
   * access token via the `refresh_token` grant proxied by the gateway (so the
   * client never holds the client secret). A DISTINCT path from the native
   * `/auth/refresh` so the two contracts never collide on one route. Not driven
   * by the SPA today (native refresh is the default strategy); pinned here so
   * the Gateway-owned SSO contract is covered and separated from the native one.
   *
   * OWNERSHIP: Tyndo.Gateway — src/Tyndo.Gateway/Endpoints/AuthEndpoints.cs.
   */
  ssoRefresh: {
    method: 'POST',
    path: '/auth/sso/refresh',
  },
} as const;

/** Request body the gateway binds for `POST /auth/refresh` ([FromBody]). */
export interface RefreshTokenRequestBody {
  refreshToken: string;
}

/** Request body the gateway binds for `POST /auth/logout` ([FromBody]). */
export interface LogoutRequestBody {
  refreshToken: string;
}

/** Success response shape for `POST /auth/refresh`. */
export interface RefreshTokenResponse {
  token: string;
  refreshToken?: string | null;
  expiresIn?: number | null;
}

/** Response shape for `GET /auth/me`. */
export interface AuthMeResponse {
  userId: string;
  permissions: string[];
  roles: string[];
}

/**
 * Reconstruct the full gateway path (with `/api/v1` prefix) for a base-relative
 * auth endpoint path. The contract test compares this against the gateway's
 * published routes so a frontend path that the gateway does not expose fails
 * the build instead of 404-ing in production.
 */
export const gatewayPath = (baseRelativePath: string): string =>
  `${GATEWAY_API_PREFIX}${baseRelativePath}`;
